import React, { useCallback, useEffect } from "react";
import { BrowserRouter, Route } from "react-router-dom";
import { initializeIcons } from "@fluentui/font-icons-mdl2";
import { Stack, ThemeProvider, loadTheme } from "@fluentui/react";
import { darkTheme, lightTheme } from "../../themes";
import "../../styles/App.css";
import styles from "./BaseLayout.module.scss";
import { RouteType } from "../../types";
import { generateThemeCssVars } from "../../utils/generateColorVariables";
import { useSyncedLocalStorage } from "../../hooks";

initializeIcons();

type ThemeMode = "light" | "dark";

const themeStorageKey = "eas.theme";
const legacyThemeStorageKey = "isDarkTheme";

const isThemeMode = (value: string): value is ThemeMode =>
  value === "dark" || value === "light";

const getInitialTheme = (): ThemeMode => {
  const sharedTheme = localStorage.getItem(themeStorageKey);
  if (sharedTheme === "dark" || sharedTheme === "light") {
    return sharedTheme;
  }

  return localStorage.getItem(legacyThemeStorageKey) === "true"
    ? "dark"
    : "light";
};

const readTheme = (value: string): ThemeMode => {
  if (!isThemeMode(value)) {
    throw new Error(`Unsupported theme mode: ${value}`);
  }

  return value;
};

export const BaseLayout: React.FunctionComponent<{
  basename?: string;
  pages: RouteType[];
  renderNavigationMenu: (
    isDarkTheme: boolean,
    setIsDarkTheme: (isDarkTheme: boolean) => void,
  ) => JSX.Element;
  renderErrorHeader?: () => JSX.Element;
  navigationPlacement?: "side" | "top";
}> = ({
  basename,
  pages,
  renderNavigationMenu,
  renderErrorHeader,
  navigationPlacement = "side",
}) => {
  const [theme, setTheme] = useSyncedLocalStorage<ThemeMode>(
    themeStorageKey,
    getInitialTheme,
    {
      deserialize: readTheme,
      serialize: (value) => value,
    },
  );
  const isDarkTheme = theme === "dark";
  const setIsDarkTheme = useCallback(
    (nextIsDark: boolean) => {
      localStorage.setItem(legacyThemeStorageKey, String(nextIsDark));
      setTheme(nextIsDark ? "dark" : "light");
    },
    [setTheme],
  );
  const isTopNavigation = navigationPlacement === "top";
  const isEmbedded = window.self !== window.top;

  useEffect(() => {
    document.body.className = `theme-${isDarkTheme ? "dark" : "light"}`;
    const themeVarsStyle = document.createElement("style");
    themeVarsStyle.id = "sculpt-theme-vars";
    themeVarsStyle.innerHTML = `:root { 
        color-scheme: ${isDarkTheme ? "dark" : "light"};
        ${generateThemeCssVars(isDarkTheme ? darkTheme : lightTheme)} 
      }`;
    document.head.appendChild(themeVarsStyle);
    loadTheme(isDarkTheme ? darkTheme : lightTheme);
    return () => {
      document.head.removeChild(themeVarsStyle);
    };
  }, [isDarkTheme]);

  return (
    <BrowserRouter basename={basename}>
      <ThemeProvider
        applyTo="body"
        theme={isDarkTheme ? darkTheme : lightTheme}
        className={isDarkTheme ? "darkTheme" : "lightTheme"}
      >
        {renderErrorHeader && renderErrorHeader()}
        <div
          className={`${styles.globalWrapper} ${
            isTopNavigation ? styles.globalWrapperTop : ""
          }`}
        >
          <Stack
            className={`${styles.navigationWrapper} ${
              isTopNavigation ? styles.navigationWrapperTop : ""
            }`}
          >
            {renderNavigationMenu(isDarkTheme, setIsDarkTheme)}
          </Stack>
          <div
            className={`${styles.routerWrapper} ${
              isTopNavigation ? styles.routerWrapperTop : ""
            } ${isEmbedded ? styles.routerWrapperEmbedded : ""}`}
          >
            {pages.map((page, index) => (
              <Route
                key={index}
                exact={page.exact}
                render={page.render}
                component={page.component}
                path={page.path}
              ></Route>
            ))}
          </div>
        </div>
      </ThemeProvider>
    </BrowserRouter>
  );
};
